/**
 * NotesPage - Personal notes with markdown support
 */

import { useState, useMemo, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import { useNotesStore, type Note } from '@/stores/notes-store';
import { useAuthStore } from '@/stores/auth-store';
import { useFamilyStore, useFamilyHydrated } from '@/stores/family-store';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';
import {
  Plus,
  Search,
  X,
  Trash2,
  Edit2,
  Clock,
  FileText,
  HelpCircle,
  Pin,
  LayoutGrid,
  Rows3,
  Lock,
  FileStack,
  ChevronDown,
  Download,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import DOMPurify from 'dompurify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { marked } from 'marked';
import { getNativePlatform } from '@/utils/native-platform';

// Helper to convert markdown tables with <br /> to HTML tables
const convertTablesThatNeedBr = (markdown: string): { type: 'markdown' | 'html', content: string }[] => {
  // Check if markdown contains both a table and <br tags
  if (!markdown.includes('|') || !markdown.includes('<br')) {
    return [{ type: 'markdown', content: markdown }];
  }
  
  // Split by lines and find table blocks
  const lines = markdown.split('\n');
  const result: { type: 'markdown' | 'html', content: string }[] = [];
  let i = 0;
  let markdownBuffer: string[] = [];
  
  const flushMarkdown = () => {
    if (markdownBuffer.length > 0) {
      result.push({ type: 'markdown', content: markdownBuffer.join('\n') });
      markdownBuffer = [];
    }
  };
  
  while (i < lines.length) {
    const line = lines[i];
    
    // Check if this line starts a table (contains |)
    if (line.includes('|') && line.trim().startsWith('|')) {
      // Collect all table lines
      const tableLines: string[] = [];
      let tableHasBr = false;
      
      while (i < lines.length && lines[i].includes('|')) {
        tableLines.push(lines[i]);
        if (lines[i].includes('<br')) {
          tableHasBr = true;
        }
        i++;
      }
      
      // If table has <br>, convert to HTML
      if (tableHasBr && tableLines.length >= 3) {
        flushMarkdown();
        
        const headerLine = tableLines[0];
        const bodyLines = tableLines.slice(2); // Skip separator line
        
        // Parse header
        const headers = headerLine.split('|').filter(c => c.trim()).map(h => h.trim());
        
        // Build HTML table with prose-compatible styling
        let html = '<table>\n<thead>\n<tr>\n';
        headers.forEach(h => {
          html += `<th>${h}</th>\n`;
        });
        html += '</tr>\n</thead>\n<tbody>\n';
        
        // Parse body rows
        bodyLines.forEach(line => {
          if (!line.trim() || line.includes('---')) return;
          const cells = line.split('|').filter(c => c.trim()).map(c => c.trim());
          html += '<tr>\n';
          cells.forEach(cell => {
            html += `<td>${cell}</td>\n`;
          });
          html += '</tr>\n';
        });
        
        html += '</tbody>\n</table>';
        result.push({ type: 'html', content: html });
      } else {
        // Keep original markdown table
        markdownBuffer.push(tableLines.join('\n'));
      }
    } else {
      markdownBuffer.push(line);
      i++;
    }
  }
  
  flushMarkdown();
  return result;
};

// Allow <br /> tags in sanitized output
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'br', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes?.['*'] || [])],
  },
};

// Note templates
interface NoteTemplate {
  title: string;
  content: string;
  tags: string[];
}

const noteTemplates: NoteTemplate[] = [
  {
    title: 'Nödnummer',
    content: `# Viktiga telefonnummer och kontakter

## Allmänna nödnummer
- **112** - SOS Alarm (polis, ambulans, brandkår)
- **113 13** - Giftinformationscentralen
- **1177** - Vårdguiden (sjukvårdsrådgivning)
- **114 14** - Polisen (icke-akut)

## Viktiga hemsidor
- **krisinformation.se** - Officiell information vid kriser
- **dinsakerhet.se** - Beredskapsinformation
- **msb.se** - Myndigheten för samhällsskydd och beredskap

## Familj och nära och kära
| Namn | Relation | Telefon | E-post |
|------|----------|---------|--------|
|      |          |         |        |
|      |          |         |        |
|      |          |         |        |

## Förskola/Skola
| Institution | Kontaktperson | Telefon | E-post |
|-------------|---------------|---------|--------|
|             |               |         |        |

## Försäkringsbolag
- **Bolag:** 
- **Försäkringsnummer:** 
- **Telefon:** 
- **Skadetelefon:** 

## Bilbergning
- **Bolag:** 
- **Medlemsnummer:** 
- **Telefon:** 

## Övriga viktiga kontakter
| Kontakt | Syfte | Telefon | E-post |
|---------|-------|---------|--------|
|         |       |         |        |`,
    tags: ['nödnummer', 'kontakter', 'viktig-information']
  },
  {
    title: 'Familjeplan vid kris',
    content: `# Familjeplan vid kris

## Samlingsplats
**Primär samlingsplats:** 
- Adress: 
- Beskrivning: 

**Alternativ samlingsplats:** 
- Adress: 
- Beskrivning: 

## Kommunikationsplan
**Om telefonnätet är nere:**
- Kontaktperson utanför området: 
- Telefon: 
- E-post: 

**Viktig information:**
- Lyssna på Sveriges Radio P4 på FM-radio
- Följ krisinformation.se
- Använd SMS istället för samtal (belastar nätet mindre)

## Ansvar och uppgifter
| Familjemedlem | Ansvar | Kontaktuppgift |
|---------------|--------|----------------|
|               |        |                |
|               |        |                |

## Viktiga dokument
**Vart förvaras:**
- ID-handlingar: 
- Försäkringshandlingar: 
- Vigselbevis/födelseattest: 
- Medicinska journaler: 

## Husdjur
- Namn: 
- Art/ras: 
- Medicin/särskilda behov: 
- Veterinär: 

## Att komma ihåg
- [ ] Ha alltid mobiltelefoner laddade
- [ ] Håll kontantreserv hemma
- [ ] Vet alla familjemedlemmar samlingsplatsen?
- [ ] Öva på planen minst en gång per år`,
    tags: ['krisplan', 'familj', 'beredskap']
  },
  {
    title: 'Beredskapsplan',
    content: `# Övergripande beredskapsplan

## Beredskapsmål
**Nuvarande mål:** 7/14/30/90 dagar (rensa bort det som inte gäller)

## Vatten
**Strategi:**
- Antal liter i förråd: 
- Vattenreningstabletter: Ja/Nej
- Vattenfilter: Ja/Nej
- Alternativa vattenkällor: 

**Dagligt behov:**
- 3 liter per person för dricka och mat
- Extra för hygien och disk

## Mat
**Förråd:**
- Torrvaror (pasta, ris, gryn): 
- Konserver: 
- Knäckebröd/hårt bröd: 
- Näringstätt (müsli, nötter): 
- Tillagning utan el/gas: Ja/Nej (beskriv)

**Viktigt att tänka på:**
- Rotera förrådet - äta och fylla på
- Allergivänligt/specialkost: 
- Husdjursmat: 

## Värme och ljus
**Utrustning:**
- Ficklampa med batterier: 
- Stearinljus/värmeljus: 
- Tändstickor/tändare: 
- Alternativ uppvärmning: 

## Hygien och medicin
**Förråd:**
- Toalettpapper: 
- Tvål/handsprit: 
- Tandkräm/tandborste: 
- Första hjälpen-kit: Ja/Nej
- Receptbelagd medicin (30 dagars förråd): 
- Receptfria läkemedel: 

## Kommunikation och information
- Radio (batteridriven/vevradio): 
- Reservbatterier/powerbank: 
- Kontantreserv: 

## Strategi och prioriteringar
**Kortfristiga åtgärder (7 dagar):**
1. 
2. 
3. 

**Medelfristiga åtgärder (30 dagar):**
1. 
2. 
3. 

**Långsiktiga mål:**
1. 
2. 
3. 

## Genomförande
**Nästa steg:**
- [ ] 
- [ ] 
- [ ] 

**Budget:** 

**Tidsplan:** 

**Ansvarig:** `,
    tags: ['beredskapsplan', 'strategi', 'planering']
  }
];

export default function NotesPage() {
  const nativePlatform = getNativePlatform();
  const isNativeIOS = nativePlatform === 'ios';

  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const family = useFamilyStore((state) => state.family);
  const familyHydrated = useFamilyHydrated();
  const familyId = family?.id;
  
  // Start with loading state to prevent flash of wrong content
  const [isLoadingNotes, setIsLoadingNotes] = useState(true);
  
  const {
    notes,
    totalCount,
    fetchFromServer,
    addNote,
    updateNote,
    deleteNote,
    togglePin,
    searchNotes,
  } = useNotesStore();
  
  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [viewingNote, setViewingNote] = useState<Note | null>(null);
  const [confirmDeleteNote, setConfirmDeleteNote] = useState<Note | null>(null);
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumFeature, setPremiumFeature] = useState<string>('');
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [gridColumns, setGridColumns] = useState<'single' | 'gallery'>(() => {
    try {
      const stored = localStorage.getItem('notesGridColumns');
      return (stored === 'single' || stored === 'gallery') ? stored : 'gallery';
    } catch {
      return 'gallery';
    }
  });
  
  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formTags, setFormTags] = useState<string[]>([]);
  const [formIsPrivate, setFormIsPrivate] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showTagPicker, setShowTagPicker] = useState(false);
  
  // Premium limits
  const FREE_TIER_LIMIT = 2;
  const PREMIUM_LIMIT = 500;
  const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;
  const currentLimit = hasPremiumAccess ? PREMIUM_LIMIT : FREE_TIER_LIMIT;
  const isAtLimit = totalCount >= currentLimit;
  
  // Fetch notes on mount and when family changes
  useEffect(() => {
    console.log('[NotesPage] useEffect triggered:', { 
      familyHydrated, 
      isAuthenticated, 
      userFamilyId: user?.familyId,
      familyId,
    });
    
    // Don't fetch until authenticated and family hydrated
    if (!familyHydrated || !isAuthenticated) {
      console.log('[NotesPage] Waiting for family hydration or authentication');
      return;
    }
    
    // CRITICAL: If user has a familyId but family hasn't loaded yet, wait!
    if (user?.familyId && !familyId) {
      console.log('[NotesPage] User has familyId but family not loaded yet - waiting...');
      return;
    }
    
    // Always clear notes first to prevent showing stale data
    useNotesStore.setState({ notes: [], totalCount: 0 });
    setIsLoadingNotes(true);
    
    console.log('[NotesPage] Calling fetchFromServer');
    fetchFromServer().finally(() => {
      setIsLoadingNotes(false);
    });
  }, [familyHydrated, isAuthenticated, user?.familyId, familyId, fetchFromServer]);

  // Cleanup: clear notes only when leaving the page
  useEffect(() => {
    return () => {
      useNotesStore.setState({ notes: [], totalCount: 0 });
    };
  }, []);

  // Save grid columns preference to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('notesGridColumns', gridColumns);
    } catch {
      // noop
    }
  }, [gridColumns]);

  // Open specific note from Dashboard navigation
  useEffect(() => {
    const state = location.state as { openNoteId?: string } | null;
    if (state?.openNoteId && notes.length > 0) {
      const noteToOpen = notes.find(n => n.id === state.openNoteId);
      if (noteToOpen) {
        setViewingNote(noteToOpen);
        // Clear the state so it doesn't reopen on subsequent visits
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, notes]);
  
  // Scroll locking for modals
  useLayoutEffect(() => {
    const isModalOpen = showAddModal || !!editingNote || !!viewingNote || !!confirmDeleteNote || showMarkdownHelp;
    const previousBodyOverflow = document.body.style.overflow;
    const previousDocOverflow = document.documentElement.style.overflow;

    if (isModalOpen) {
      if (isNativeIOS) {
        try {
          document.body.style.overflow = 'hidden';
          document.documentElement.style.overflow = 'hidden';
        } catch (_e) {
          void _e;
        }
      } else {
        const y = window.scrollY || window.pageYOffset || 0;
        try { document.body.dataset.prevScrollY = String(y); } catch (_e) { void _e; }
        try { lockScroll(); } catch (_e) { try { document.body.style.overflow = 'hidden'; } catch { /* noop */ } }
        try { requestAnimationFrame(() => { try { window.scrollTo(0, y); } catch { /* noop */ } }); } catch { /* noop */ }
      }
    } else {
      if (isNativeIOS) {
        try {
          document.body.style.overflow = previousBodyOverflow;
          document.documentElement.style.overflow = previousDocOverflow;
        } catch (_e) {
          void _e;
        }
      } else {
        try { unlockScroll(); } catch (_e) { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
      }
    }
    
    return () => {
      if (isNativeIOS) {
        try {
          document.body.style.overflow = previousBodyOverflow;
          document.documentElement.style.overflow = previousDocOverflow;
        } catch (_e) {
          void _e;
        }
      } else {
        try { unlockScroll(); } catch (_e) { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
      }
    };
  }, [confirmDeleteNote, editingNote, isNativeIOS, showAddModal, showMarkdownHelp, viewingNote]);
  
  // Filter notes - with SAFETY CHECK for family members
  const filteredNotes = useMemo(() => {
    let result = notes;
    
    // CRITICAL SAFETY FILTER: Remove member's own non-private notes
    if (family && user && family.ownerId !== user.id) {
      console.log('[NotesPage] Member safety filter - checking', result.length, 'notes');
      result = result.filter(note => {
        // Keep all notes not created by this member
        if (note.authorId !== user.id) {
          console.log('[NotesPage] Keeping note from', note.authorName);
          return true;
        }
        
        // Keep member's own private notes
        if (note.isPrivate) {
          console.log('[NotesPage] Keeping member\'s private note:', note.title);
          return true;
        }
        
        // Remove member's own non-private notes (SHOULD NEVER HAPPEN)
        console.log('[NotesPage] ⚠️ FILTERING OUT member\'s non-private note:', note.title);
        return false;
      });
      console.log('[NotesPage] After safety filter:', result.length, 'notes');
    }
    
    console.log('[NotesPage] Starting with', result.length, 'notes after safety filter');
    
    if (searchQuery) {
      result = searchNotes(searchQuery);
      console.log('[NotesPage] After search:', result.length, 'notes');
    }
    
    if (selectedTag) {
      result = result.filter((n) => n.tags.includes(selectedTag));
      console.log('[NotesPage] After tag filter:', result.length, 'notes');
    }
    
    // Sort: pinned first (alphabetically), then unpinned (by date)
    return result.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      if (a.isPinned && b.isPinned) {
        return a.title.localeCompare(b.title);
      }
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [notes, searchQuery, selectedTag, searchNotes, family, user]);
  
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    for (const note of notes) {
      for (const tag of note.tags) {
        const trimmedTag = tag.trim();
        if (trimmedTag) tags.add(trimmedTag);
      }
    }
    return Array.from(tags).sort((a, b) => a.localeCompare(b, 'sv-SE'));
  }, [notes]);
  
  // Handlers
  const openAddModal = () => {
    if (isAtLimit) {
      setPremiumFeature(
        hasPremiumAccess
          ? `Du har nått maxgränsen på ${currentLimit} anteckningar`
          : `Du har skapat ${totalCount} av ${currentLimit} tillåtna anteckningar. Uppgradera till Premium för upp till ${PREMIUM_LIMIT} anteckningar.`
      );
      setShowPremiumModal(true);
      return;
    }
    setFormTitle('');
    setFormContent('');
    setFormTags([]);
    setFormIsPrivate(false);
    setShowAddModal(true);
  };
  
  const openEditModal = (note: Note) => {
    setFormTitle(note.title);
    setFormContent(note.content);
    setFormTags(note.tags);
    setEditingNote(note);
  };

  const closeEditorModal = () => {
    setShowAddModal(false);
    setEditingNote(null);
    setFormTitle('');
    setFormContent('');
    setFormTags([]);
    setFormIsPrivate(false);
    setTagInput('');
    setShowTagPicker(false);
    setShowTemplateDropdown(false);
  };
  
  const closeModals = () => {
    setShowAddModal(false);
    setEditingNote(null);
    setViewingNote(null);
    setConfirmDeleteNote(null);
    setFormTitle('');
    setFormContent('');
    setFormTags([]);
    setFormIsPrivate(false);
    setTagInput('');
    setShowTemplateDropdown(false);
  };
  
  const applyTemplate = (template: NoteTemplate) => {
    setFormTitle(template.title);
    setFormContent(template.content);
    setFormTags(template.tags);
    setShowTemplateDropdown(false);
  };
  
  const handleSaveNote = async () => {
    if (!formTitle.trim() || !formContent.trim()) return;
    
    try {
      if (editingNote) {
        const editingNoteId = editingNote.id;
        await updateNote(editingNote.id, {
          title: formTitle,
          content: formContent,
          tags: formTags,
        });
        const updatedNote = useNotesStore.getState().getNoteById(editingNoteId);
        if (updatedNote && viewingNote?.id === editingNoteId) {
          setViewingNote(updatedNote);
        }
      } else {
        await addNote({
          title: formTitle,
          content: formContent,
          tags: formTags,
          isPinned: false,
          isPrivate: formIsPrivate,
        });
      }
      closeEditorModal();
    } catch (error) {
      console.error('Failed to save note:', error);
    }
  };
  
  const handleDeleteNote = async () => {
    if (!confirmDeleteNote) return;
    
    try {
      await deleteNote(confirmDeleteNote.id);
      closeModals();
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };
  
  const handleDownloadPDF = async () => {
    if (!viewingNote) return;
    
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;
      
      // Helper to add new page if needed
      const checkPageBreak = (requiredSpace: number) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };
      
      // Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      const titleLines = pdf.splitTextToSize(viewingNote.title, maxWidth);
      checkPageBreak(titleLines.length * 7);
      pdf.text(titleLines, margin, yPosition);
      yPosition += titleLines.length * 7 + 5;
      
      // Date
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100);
      const dateText = new Date(viewingNote.updatedAt).toLocaleDateString('sv-SE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      checkPageBreak(5);
      pdf.text(dateText, margin, yPosition);
      yPosition += 5;
      
      // Tags
      if (viewingNote.tags.length > 0) {
        checkPageBreak(5);
        pdf.text('Taggar: ' + viewingNote.tags.join(', '), margin, yPosition);
        yPosition += 8;
      } else {
        yPosition += 3;
      }
      
      pdf.setTextColor(0);
      
      // Helper to render text with inline markdown formatting
      const renderInlineText = (text: string, x: number, y: number, maxW: number): number => {
        // Remove HTML tags first
        text = text.replace(/<[^>]*>/g, '');
        
        // Split text into segments with formatting
        const segments: { text: string; bold: boolean; italic: boolean }[] = [];
        let currentText = '';
        let i = 0;
        let isBold = false;
        let isItalic = false;
        
        while (i < text.length) {
          const prevChar = i > 0 ? text[i - 1] : ' ';
          
          // Check for **bold**
          if (text[i] === '*' && text[i + 1] === '*') {
            const afterChars = text[i + 2];
            const beforeChar = currentText.length > 0 ? currentText[currentText.length - 1] : prevChar;
            
            // Opening ** : at word boundary or after space, followed by non-space
            // Closing ** : after non-space, at word boundary or before space/punctuation/end
            const canOpen = !isBold && afterChars && !/\s/.test(afterChars);
            const canClose = isBold && currentText.length > 0 && !/\s/.test(beforeChar);
            
            if (canOpen || canClose) {
              if (currentText) {
                segments.push({ text: currentText, bold: isBold, italic: isItalic });
                currentText = '';
              }
              isBold = !isBold;
              i += 2;
              continue;
            }
          }
          
          // Check for *italic* (but not **)
          if (text[i] === '*' && text[i + 1] !== '*') {
            const afterChar = text[i + 1];
            const beforeChar = currentText.length > 0 ? currentText[currentText.length - 1] : prevChar;
            
            // Opening * : at word boundary or after space, followed by non-space
            // Closing * : after non-space, at word boundary or before space/punctuation/end
            const canOpen = !isItalic && afterChar && !/\s/.test(afterChar);
            const canClose = isItalic && currentText.length > 0 && !/\s/.test(beforeChar);
            
            if (canOpen || canClose) {
              if (currentText) {
                segments.push({ text: currentText, bold: isBold, italic: isItalic });
                currentText = '';
              }
              isItalic = !isItalic;
              i += 1;
              continue;
            }
          }
          
          currentText += text[i];
          i++;
        }
        
        if (currentText) {
          segments.push({ text: currentText, bold: isBold, italic: isItalic });
        }
        
        // Render segments
        let currentX = x;
        let currentY = y;
        
        for (const segment of segments) {
          if (!segment.text) continue;
          
          // Set font style
          const style = segment.bold && segment.italic ? 'bolditalic' : 
                       segment.bold ? 'bold' : 
                       segment.italic ? 'italic' : 'normal';
          pdf.setFont('helvetica', style);
          
          // Split into lines if needed
          const lines = pdf.splitTextToSize(segment.text, maxW - (currentX - x));
          
          for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
            const line = lines[lineIdx];
            
            if (lineIdx > 0) {
              currentY += 5;
              currentX = x;
            }
            
            pdf.text(line, currentX, currentY);
            
            // Move X position for next segment on same line
            if (lineIdx === lines.length - 1) {
              currentX += pdf.getTextWidth(line);
            }
          }
        }
        
        // Reset font
        pdf.setFont('helvetica', 'normal');
        
        return currentY;
      };
      
      // Parse markdown and render content
      const tokens = marked.lexer(viewingNote.content);
      
      for (const token of tokens) {
        switch (token.type) {
          case 'heading': {
            checkPageBreak(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(token.depth === 1 ? 16 : token.depth === 2 ? 14 : 12);
            const headingLines = pdf.splitTextToSize(token.text, maxWidth);
            pdf.text(headingLines, margin, yPosition);
            yPosition += headingLines.length * (token.depth === 1 ? 6 : token.depth === 2 ? 5.5 : 5) + 4;
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(11);
            break;
          }
            
          case 'paragraph': {
            checkPageBreak(8);
            // Process text with inline formatting
            const text = token.text.replace(/<[^>]*>/g, ''); // Remove HTML tags
            const lines = text.split('\n');
            
            for (const line of lines) {
              if (!line.trim()) {
                yPosition += 3;
                continue;
              }
              checkPageBreak(5);
              yPosition = renderInlineText(line, margin, yPosition, maxWidth);
              yPosition += 5;
            }
            yPosition += 3;
            break;
          }
            
          case 'list': {
            for (const item of token.items) {
              checkPageBreak(6);
              const bullet = token.ordered ? `${item.index || 1}. ` : '• ';
              
              // Render bullet
              pdf.text(bullet, margin + 2, yPosition);
              
              // Render item text with inline formatting
              const endY = renderInlineText(item.text, margin + 8, yPosition, maxWidth - 8);
              yPosition = endY + 5;
            }
            yPosition += 2;
            break;
          }
            
          case 'table': {
            checkPageBreak(20);
            const tableData = token.rows.map((row: any) => 
              row.map((cell: any) => cell.text.replace(/<[^>]*>/g, ''))
            );
            const headers = token.header.map((cell: any) => cell.text.replace(/<[^>]*>/g, ''));
            
            autoTable(pdf, {
              startY: yPosition,
              head: [headers],
              body: tableData,
              margin: { left: margin, right: margin },
              styles: { fontSize: 9, cellPadding: 2 },
              headStyles: { fillColor: [80, 80, 80], textColor: 255 },
              alternateRowStyles: { fillColor: [245, 245, 245] },
              didDrawPage: (data: any) => {
                yPosition = data.cursor.y + 5;
              },
            });
            yPosition = (pdf as any).lastAutoTable.finalY + 5;
            break;
          }
            
          case 'code': {
            checkPageBreak(15);
            pdf.setFont('courier', 'normal');
            pdf.setFontSize(9);
            pdf.setFillColor(240, 240, 240);
            const codeLines = token.text.split('\n');
            const codeHeight = codeLines.length * 4 + 4;
            checkPageBreak(codeHeight);
            pdf.rect(margin, yPosition - 2, maxWidth, codeHeight, 'F');
            for (const line of codeLines) {
              pdf.text(line, margin + 2, yPosition + 2);
              yPosition += 4;
            }
            yPosition += 6;
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(11);
            break;
          }
            
          case 'blockquote': {
            checkPageBreak(10);
            pdf.setFont('helvetica', 'italic');
            pdf.setTextColor(60);
            pdf.setDrawColor(200);
            pdf.line(margin, yPosition - 2, margin, yPosition + 10);
            const quoteLines = pdf.splitTextToSize(token.text.replace(/<[^>]*>/g, ''), maxWidth - 8);
            for (const line of quoteLines) {
              checkPageBreak(5);
              pdf.text(line, margin + 5, yPosition);
              yPosition += 5;
            }
            yPosition += 3;
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(0);
            break;
          }
            
          case 'hr': {
            checkPageBreak(5);
            pdf.setDrawColor(180);
            pdf.line(margin, yPosition, pageWidth - margin, yPosition);
            yPosition += 5;
            break;
          }
            
          case 'space':
            yPosition += 2;
            break;
        }
      }
      
      // Download
      const fileName = `${viewingNote.title.replace(/[^a-zA-Z0-9åäöÅÄÖ ]/g, '_')}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    }
  };
  
  const addTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !formTags.includes(trimmed) && formTags.length < 10) {
      setFormTags([...formTags, trimmed]);
      setTagInput('');
    }
  };
  
  const removeTag = (tag: string) => {
    setFormTags(formTags.filter((t) => t !== tag));
  };
  
  if (!isAuthenticated) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Du måste vara inloggad för att se anteckningar.</p>
      </div>
    );
  }

  // Show skeleton loader while family is hydrating OR while we're loading notes
  if (!familyHydrated || isLoadingNotes) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div>
          <div className="h-9 w-48 bg-muted rounded animate-pulse mb-2" />
          <div className="h-5 w-96 bg-muted rounded animate-pulse" />
        </div>

        {/* Search bar skeleton */}
        <div className="flex gap-2">
          <div className="flex-1 h-10 bg-muted rounded-lg animate-pulse" />
          <div className="h-10 w-10 bg-muted rounded-lg animate-pulse" />
          <div className="h-10 w-40 bg-muted rounded-lg animate-pulse" />
        </div>

        {/* Notes grid skeleton */}
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="rounded-lg border border-border bg-card p-4 space-y-3">
              <div className="h-6 bg-muted rounded animate-pulse w-3/4" />
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded animate-pulse" />
                <div className="h-4 bg-muted rounded animate-pulse w-5/6" />
                <div className="h-4 bg-muted rounded animate-pulse w-4/6" />
              </div>
              <div className="flex gap-2">
                <div className="h-6 w-16 bg-muted rounded animate-pulse" />
                <div className="h-6 w-20 bg-muted rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center"><FileText className="h-6 w-6 text-primary mr-3 shrink-0" />Anteckningar</h1>
        <p className="text-muted-foreground mt-1">Spara beredskapsanteckningar och annan viktig information.</p>
      </div>

      {/* Search and Add button */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Sök anteckningar..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 rounded-lg border border-border bg-background text-foreground"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Grid/List toggle - only show on md screens and up */}
        <div className="hidden md:flex items-center gap-1 bg-muted rounded-lg p-1">
          <button
            onClick={() => setGridColumns('gallery')}
            className={`p-2 rounded transition-colors ${
              gridColumns === 'gallery'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Galleri (3 kolumner)"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setGridColumns('single')}
            className={`p-2 rounded transition-colors ${
              gridColumns === 'single'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Enkel kolumn"
          >
            <Rows3 className="h-4 w-4" />
          </button>
        </div>
        
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-colors"
          style={{ backgroundColor: '#67724e' }}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {/* Tag filter */}
      {allTags.length > 0 && (
        <div className="flex gap-2 flex-wrap overflow-x-auto">
          <button
            onClick={() => setSelectedTag(null)}
            className="px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all"
            style={
              !selectedTag
                ? {
                    backgroundColor: 'rgba(232, 180, 180, 0.15)',
                    color: '#e8b4b4',
                    border: '2px solid #e8b4b4',
                    boxShadow: '0 0 15px rgba(232, 180, 180, 0.6), 0 0 25px rgba(232, 180, 180, 0.4)',
                  }
                : {
                    backgroundColor: 'rgba(232, 180, 180, 0.05)',
                    color: '#e8b4b4',
                    border: '1px solid rgba(232, 180, 180, 0.3)',
                  }
            }
            onMouseEnter={(e) => {
              if (selectedTag) {
                e.currentTarget.style.backgroundColor = 'rgba(232, 180, 180, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(232, 180, 180, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTag) {
                e.currentTarget.style.backgroundColor = 'rgba(232, 180, 180, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(232, 180, 180, 0.3)';
              }
            }}
          >
            Alla
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
              className="px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all"
              style={
                selectedTag === tag
                  ? {
                      backgroundColor: 'rgba(232, 180, 180, 0.15)',
                      color: '#e8b4b4',
                      border: '2px solid #e8b4b4',
                      boxShadow: '0 0 15px rgba(232, 180, 180, 0.6), 0 0 25px rgba(232, 180, 180, 0.4)',
                    }
                  : {
                      backgroundColor: 'rgba(232, 180, 180, 0.05)',
                      color: '#e8b4b4',
                      border: '1px solid rgba(232, 180, 180, 0.3)',
                    }
              }
              onMouseEnter={(e) => {
                if (selectedTag !== tag) {
                  e.currentTarget.style.backgroundColor = 'rgba(232, 180, 180, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(232, 180, 180, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTag !== tag) {
                  e.currentTarget.style.backgroundColor = 'rgba(232, 180, 180, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(232, 180, 180, 0.3)';
                }
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}
      
      {/* Notes List */}
      <div>
        {filteredNotes.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchQuery || selectedTag
                ? 'Inga anteckningar matchar din sökning'
                : 'Inga anteckningar ännu.'}
            </p>
          </div>
        ) : (
          <div className={`grid gap-4 ${gridColumns === 'gallery' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                className="rounded-lg border border-border bg-card p-4 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setViewingNote(note)}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {note.isPrivate && (
                      <Lock className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                    )}
                    <h3 className="font-semibold text-foreground line-clamp-2">{note.title}</h3>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!note.isPrivate && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePin(note.id);
                        }}
                        className="p-1.5 rounded hover:bg-muted transition-colors"
                        title={note.isPinned ? 'Ta bort pin' : 'Fäst'}
                      >
                        <Pin className={`h-4 w-4 ${note.isPinned ? 'fill-current' : ''}`} style={{ color: note.isPinned ? '#e8b4b4' : undefined }} />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(note);
                      }}
                      className="p-1.5 rounded hover:bg-muted transition-colors"
                      title="Redigera"
                    >
                      <Edit2 className="h-4 w-4 text-muted-foreground" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteNote(note);
                      }}
                      className="p-1.5 rounded hover:bg-muted transition-colors"
                      title="Radera"
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                  {note.content
                    .replace(/[#*_`~[\]()]/g, '')
                    .replace(/!\[.*?\]\(.*?\)/g, '')
                    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
                    .replace(/\|/g, ' ')
                    .replace(/[-:]{3,}/g, '')
                    .replace(/<[^>]*>/g, '')
                    .substring(0, 150)}...
                </p>
                
                {note.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {note.tags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTag((current) => (current === tag ? null : tag));
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-transparent transition-colors"
                        style={
                          selectedTag === tag
                            ? {
                                backgroundColor: 'rgba(232, 180, 180, 0.15)',
                                color: '#e8b4b4',
                                borderColor: 'rgba(232, 180, 180, 0.9)',
                              }
                            : {
                                backgroundColor: 'rgba(232, 180, 180, 0.06)',
                                color: '#e8b4b4',
                                borderColor: 'rgba(232, 180, 180, 0.4)',
                              }
                        }
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {new Date(note.updatedAt).toLocaleDateString('sv-SE', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </div>
                {family && note.lastModifiedBy && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {note.lastModifiedBy}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Add/Edit Modal */}
      {(showAddModal || editingNote) && createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}>
          <div className="bg-background rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] m-4 overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">
                  {editingNote ? 'Redigera anteckning' : 'Ny anteckning'}
                </h2>
                <button
                  onClick={closeEditorModal}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Template button - only show when creating new note */}
              {!editingNote && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-background hover:bg-muted transition-colors text-sm font-medium"
                  >
                    <FileStack className="h-4 w-4" />
                    Mallar
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  
                  {/* Template dropdown */}
                  {showTemplateDropdown && (
                    <div className="absolute top-full mt-2 left-0 w-72 bg-background border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                      <div className="py-1">
                        {noteTemplates.map((template, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => applyTemplate(template)}
                            className="w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-border last:border-b-0"
                          >
                            <div className="font-medium text-foreground mb-1">{template.title}</div>
                            <div className="text-xs text-muted-foreground">
                              {template.tags.join(', ')}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Titel
                  <span className="ml-2 text-xs text-muted-foreground">{formTitle.length}/100</span>
                </label>
                <input
                  type="text"
                  maxLength={100}
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="Min anteckning..."
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                />
              </div>
              
              <div>
                <label className="flex items-center gap-1 text-sm font-medium text-foreground mb-2">
                  Innehåll (Markdown)
                  <button
                    type="button"
                    onClick={() => setShowMarkdownHelp(true)}
                    className="text-primary hover:text-primary/80 transition-colors"
                    title="Visa Markdown-hjälp"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>
                  <span className="ml-auto text-xs text-muted-foreground">{formContent.length}/5000</span>
                </label>
                <textarea
                  maxLength={5000}
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  placeholder="Skriv din anteckning här... Du kan använda Markdown för formatering."
                  rows={12}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground font-mono text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Taggar
                  <span className="ml-2 text-xs text-muted-foreground">{formTags.length}/10 taggar</span>
                  {tagInput.length > 0 && <span className="ml-2 text-xs text-muted-foreground">{tagInput.length}/20</span>}
                </label>

                {allTags.length > 0 && formTags.length < 10 && (
                  <div className="relative mb-2">
                    <button
                      type="button"
                      onClick={() => setShowTagPicker((prev) => !prev)}
                      className="w-full px-3 py-2 border border-border bg-input text-foreground text-sm rounded-lg flex items-center justify-between gap-2 hover:bg-muted transition-colors"
                    >
                      <span className="truncate text-left">
                        Välj befintlig tagg...
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 flex-shrink-0 transition-transform ${showTagPicker ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {showTagPicker && (
                      <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-card shadow-lg max-h-60 overflow-y-auto">
                        {allTags
                          .filter((tag) => !formTags.includes(tag))
                          .map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => {
                                if (!formTags.includes(tag) && formTags.length < 10) {
                                  setFormTags([...formTags, tag]);
                                }
                                setShowTagPicker(false);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                            >
                              {tag}
                            </button>
                          ))}
                        {allTags.filter((tag) => !formTags.includes(tag)).length === 0 && (
                          <div className="px-3 py-2 text-xs text-muted-foreground italic">
                            Inga fler taggar att välja
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    maxLength={20}
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder="Lägg till tagg..."
                    className="flex-1 px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                  />
                  <button
                    onClick={addTag}
                    className="p-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: '#67724e' }}
                    title="Lägg till tagg"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                
                {formTags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs border"
                        style={{
                          backgroundColor: 'rgba(232, 180, 180, 0.10)',
                          color: '#e8b4b4',
                          borderColor: 'rgba(232, 180, 180, 0.45)',
                        }}
                      >
                        {tag}
                        <button
                          onClick={() => removeTag(tag)}
                          className="ml-1 hover:text-red-400"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {!editingNote && (
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formIsPrivate}
                      onChange={(e) => setFormIsPrivate(e.target.checked)}
                      className="w-4 h-4 rounded border-border"
                    />
                    <Lock className="h-4 w-4 text-yellow-500" />
                    Privat anteckning
                  </label>
                  <p className="text-xs text-muted-foreground mt-1 ml-6">
                    Privata anteckningar sparas i din egen profil och syns inte för andra familjemedlemmar.
                  </p>
                </div>
              )}
            </div>
            
            <div className="sticky bottom-0 bg-background border-t border-border px-6 py-4 flex justify-end gap-2">
              <button
                onClick={closeEditorModal}
                className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleSaveNote}
                disabled={!formTitle.trim() || !formContent.trim()}
                className="px-4 py-2 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#67724e' }}
              >
                {editingNote ? 'Spara ändringar' : 'Skapa anteckning'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {viewingNote && createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}>
          <div className="bg-background rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] m-4 overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-4 sm:px-6 py-3 sm:py-4 z-10">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {viewingNote.isPrivate && (
                    <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500 flex-shrink-0" />
                  )}
                  <h2 className="text-lg sm:text-2xl font-bold text-foreground truncate">{viewingNote.title}</h2>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                  <button
                    onClick={handleDownloadPDF}
                    className="p-2 sm:p-2 rounded-lg hover:bg-muted transition-colors touch-manipulation"
                    title="Ladda ner som PDF"
                  >
                    <Download className="h-5 w-5 sm:h-5 sm:w-5" />
                  </button>
                  <button
                    onClick={() => openEditModal(viewingNote)}
                    className="p-2 sm:p-2 rounded-lg hover:bg-muted transition-colors touch-manipulation"
                    title="Redigera"
                  >
                    <Edit2 className="h-5 w-5 sm:h-5 sm:w-5" />
                  </button>
                  <button
                    onClick={() => setConfirmDeleteNote(viewingNote)}
                    className="p-2 sm:p-2 rounded-lg hover:bg-muted transition-colors touch-manipulation"
                    title="Radera"
                  >
                    <Trash2 className="h-5 w-5 sm:h-5 sm:w-5 text-red-400" />
                  </button>
                  <button
                    onClick={closeModals}
                    className="p-2 sm:p-2 rounded-lg hover:bg-muted transition-colors touch-manipulation"
                  >
                    <X className="h-5 w-5 sm:h-5 sm:w-5" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {new Date(viewingNote.updatedAt).toLocaleDateString('sv-SE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
                
                {family && viewingNote.lastModifiedBy && (
                  <div className="text-sm text-muted-foreground">
                    {viewingNote.lastModifiedBy}
                  </div>
                )}
                
                {viewingNote.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {viewingNote.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border"
                        style={{
                          backgroundColor: 'rgba(232, 180, 180, 0.06)',
                          color: '#e8b4b4',
                          borderColor: 'rgba(232, 180, 180, 0.4)',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-6">
              <div className="prose prose-invert max-w-none">
                {convertTablesThatNeedBr(viewingNote.content).map((block, idx) => {
                  if (block.type === 'html') {
                    return <div key={idx} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.content) }} />;
                  }
                  return (
                    <ReactMarkdown 
                      key={idx}
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
                      components={{
                        p: ({ node: _node, ...props }) => <p className="whitespace-pre-line" {...props} />,
                      }}
                    >
                      {block.content}
                    </ReactMarkdown>
                  );
                })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Delete Confirmation Modal */}
      {confirmDeleteNote && createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}>
          <div className="bg-background rounded-xl shadow-xl max-w-md w-full m-4 p-6">
            <h3 className="text-xl font-bold text-foreground mb-4">Radera anteckning?</h3>
            <p className="text-muted-foreground mb-6">
              Är du säker på att du vill radera "{confirmDeleteNote.title}"? Detta kan inte ångras.
            </p>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={closeModals}
                className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteNote}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Radera
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Markdown-hjälp modal */}
      {showMarkdownHelp && createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}>
          <div className="bg-background rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] m-4 overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">Markdown-formatering</h2>
                <button
                  onClick={() => setShowMarkdownHelp(false)}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Rubriker</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                    <div># Rubrik 1</div>
                    <div>## Rubrik 2</div>
                    <div>### Rubrik 3</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Textformatering</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm space-y-1">
                    <div>**fet text**</div>
                    <div>*kursiv text*</div>
                    <div>~~genomstruken text~~</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Listor</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm space-y-1">
                    <div>- Punkt 1</div>
                    <div>- Punkt 2</div>
                    <div className="mt-2">1. Första</div>
                    <div>2. Andra</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Länkar</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                    [Länktext](https://example.com)
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Kod</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm space-y-1">
                    <div>`inline kod`</div>
                    <div className="mt-2">```</div>
                    <div>kodblock</div>
                    <div>```</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Citat</h3>
                  <div className="bg-muted rounded-lg p-3 font-mono text-sm">
                    &gt; Detta är ett citat
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Premium Required Modal */}
      <PremiumRequiredModal
        open={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature={premiumFeature}
      />
    </div>
  );
}
